import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Phone, Lock, CheckCircle2, CalendarDays } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("auth_token")}` });
const fmtDate = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });

export function BookingSection({ pageId, page, onUpgrade }) {
  const [info, setInfo] = useState(null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [phone, setPhone] = useState(page?.phone || "");
  const [topic, setTopic] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback((d) => {
    axios.get(`${API}/bookings/${pageId}`, { params: d ? { date: d } : {}, headers: authHeaders() }).then(r => setInfo(r.data)).catch(() => toast.error("Kalender konnte nicht geladen werden"));
  }, [pageId]);
  useEffect(() => { load(); }, [load]);

  const pick = (d) => { setDate(d); setTime(""); load(d); };

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/bookings/${pageId}`, { date, time, phone, topic }, { headers: authHeaders() });
      toast.success("Termin angefragt – du erhältst eine Bestätigung von uns.");
      setDate(""); setTime(""); setTopic("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Buchung fehlgeschlagen"); load(date); }
    finally { setSaving(false); }
  };

  if (!info) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;
  const locked = !info.allowed;

  return (
    <div className="space-y-6" data-testid="booking-section">
      {locked && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3" data-testid="booking-locked-hint">
          <Lock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900">Telefonische Unterstützung ist in den Paketen Pro und Premium enthalten.</p>
            <p className="text-sm text-amber-800 mt-1">Dein aktuelles Paket: <strong>{info.package_name}</strong>. Mit einem Upgrade bekommst du ein persönliches Onboarding-Gespräch.</p>
            <Button size="sm" className="mt-3 rounded-full bg-indigo-600 hover:bg-indigo-700" onClick={onUpgrade} data-testid="booking-upgrade-btn">Jetzt auf Pro upgraden</Button>
          </div>
        </div>
      )}

      <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-6 ${locked ? "opacity-50 pointer-events-none select-none" : ""}`} aria-disabled={locked}>
        <div className="flex items-center gap-2 mb-1"><Phone className="w-5 h-5 text-indigo-600" /><h2 className="font-bold text-slate-900 text-lg">Telefonisches Onboarding buchen</h2></div>
        <p className="text-sm text-slate-500 mb-5">Wähle einen Wunschtermin (Mo–Fr, 09:00–17:00 Uhr). Wir rufen dich zur gewählten Zeit an.</p>

        <Label className="text-slate-700">1. Tag</Label>
        <div className="flex flex-wrap gap-2 mt-2 mb-5" data-testid="booking-dates">
          {info.dates.map(d => (
            <button key={d} onClick={() => pick(d)} disabled={locked} data-testid={`booking-date-${d}`}
              className={`px-3 py-2 rounded-xl text-sm border transition-colors ${date === d ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-200 text-slate-700 hover:border-indigo-300"}`}>
              {fmtDate(d)}
            </button>
          ))}
        </div>

        {date && (
          <>
            <Label className="text-slate-700">2. Uhrzeit</Label>
            <div className="flex flex-wrap gap-2 mt-2 mb-5" data-testid="booking-times">
              {info.times.map(t => (
                <button key={t} onClick={() => setTime(t)} data-testid={`booking-time-${t}`}
                  className={`px-3 py-2 rounded-xl text-sm border transition-colors ${time === t ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-200 text-slate-700 hover:border-indigo-300"}`}>
                  {t}
                </button>
              ))}
              {info.times.length === 0 && <p className="text-sm text-slate-500">Für diesen Tag sind alle Termine vergeben.</p>}
            </div>
          </>
        )}

        {time && (
          <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
            <div><Label>3. Telefonnummer</Label><Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+49 170 1234567" className="mt-1.5 h-11" data-testid="booking-phone" /></div>
            <div><Label>Thema (optional)</Label><Input value={topic} onChange={e => setTopic(e.target.value)} placeholder="z. B. Texte & Bilder anpassen" className="mt-1.5 h-11" data-testid="booking-topic" /></div>
            <div className="sm:col-span-2">
              <p className="text-xs text-slate-500 mb-3">Deine Telefonnummer verwenden wir ausschließlich für diesen Rückruf (Art. 6 Abs. 1 lit. b DSGVO).</p>
              <Button onClick={submit} disabled={saving || phone.trim().length < 6} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="booking-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CalendarDays className="w-4 h-4 mr-2" />Termin anfragen: {fmtDate(date)}, {time} Uhr</>}
              </Button>
            </div>
          </div>
        )}
      </div>

      {info.bookings.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6" data-testid="booking-list">
          <h3 className="font-semibold text-slate-900 mb-3">Deine Termine</h3>
          <ul className="space-y-2">
            {info.bookings.map(b => (
              <li key={b.id} className="flex items-center gap-3 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-500" />{fmtDate(b.date)}, {b.time} Uhr · {b.phone} <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full">{b.status}</span></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
